import { useCallback, useMemo, useState, useEffect } from "react";
import {
  Box,
  Input,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  IconButton,
} from "@chakra-ui/react";
import { CollecticonArrowSemiSpinCcw } from "@devseed-ui/collecticons-chakra";
import { Overline } from "../components";
import { MIN_DATE, MAX_DATE } from "../settings";

type Props = {
  minDate?: Date;
  maxDate?: Date;
  selectedDates: Date[];
  setSelectedDates: (dates: Date[]) => void;
};

function DatePicker({
  minDate = new Date(0),
  maxDate = new Date(),
  selectedDates,
  setSelectedDates,
}: Props) {
  const [selectedDatesInternal, setSelectedDatesInternal] =
    useState<Date[]>(selectedDates);

  useEffect(() => {
    setSelectedDatesInternal(selectedDates);
  }, [selectedDates]);

  const minDateMs = minDate.getTime();
  const maxDateMs = maxDate.getTime();

  const value = useMemo(
    () => [
      selectedDatesInternal[0].getTime(),
      selectedDatesInternal[1].getTime(),
    ],
    [selectedDatesInternal]
  );

  const handleChange = useCallback(([from, to]: number[]) => {
    setSelectedDatesInternal([new Date(from), new Date(to)]);
  }, []);

  const handleChangeEnd = () => {
    setSelectedDates(selectedDatesInternal);
  };

  return (
    <>
      <Box display="flex" alignItems="center" gap="2">
        <Overline>Date range:</Overline>
        <Input
          type="date"
          size="sm"
          width="fit-content"
          variant="unstyled"
          isRequired
          required
          fontWeight="bold"
          min={minDate.toISOString().split("T")[0]}
          max={
            selectedDatesInternal[1].valueOf() < maxDate.valueOf()
              ? selectedDatesInternal[1].toISOString().split("T")[0]
              : maxDate.toISOString().split("T")[0]
          }
          value={new Date(value[0]).toISOString().split("T")[0]}
          onBlur={handleChangeEnd}
          onChange={(e) => {
            if (e.target.checkValidity()) {
              handleChange([new Date(e.target.value).getTime(), value[1]]);
            }
          }}
        />
        -
        <Input
          type="date"
          size="sm"
          width="fit-content"
          variant="unstyled"
          isRequired
          required
          fontWeight="bold"
          min={
            selectedDatesInternal[0].valueOf() > minDate.valueOf()
              ? selectedDatesInternal[0].toISOString().split("T")[0]
              : minDate.toISOString().split("T")[0]
          }
          max={maxDate.toISOString().split("T")[0]}
          value={new Date(value[1]).toISOString().split("T")[0]}
          onBlur={handleChangeEnd}
          onChange={(e) => {
            if (e.target.checkValidity()) {
              handleChange([value[0], new Date(e.target.value).getTime()]);
            }
          }}
        />
        <IconButton
          size="xs"
          variant="ghost"
          colorScheme="gray"
          isDisabled={
            MIN_DATE.valueOf() === selectedDates[0].valueOf() &&
            MAX_DATE.valueOf() - 50000 < selectedDates[1].valueOf()
          }
          ml={2}
          icon={<CollecticonArrowSemiSpinCcw size="10px" />}
          onClick={() => setSelectedDates([MIN_DATE, MAX_DATE])}
          title="Reset date filter"
          aria-label="Reset date filter"
        />
      </Box>
      <RangeSlider
        aria-label={["Minimum date", "Maximum date"]}
        defaultValue={[minDateMs, maxDateMs]}
        min={minDateMs}
        max={maxDateMs}
        step={8640000} // One day on milliseconds
        value={value}
        onChange={handleChange}
        onChangeEnd={handleChangeEnd}
        colorScheme="primary"
      >
        <RangeSliderTrack>
          <RangeSliderFilledTrack bg="primary.500" />
        </RangeSliderTrack>
        <RangeSliderThumb index={0} w={4} h={4} />
        <RangeSliderThumb index={1} w={4} h={4} />
      </RangeSlider>
    </>
  );
}

export default DatePicker;
